import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import apiPaths from 'constants/api-paths';
import { appDb } from 'dexie/app.db';
import { generateURL } from 'helpers/api-path.helper';
import {
  switchMap,
  from,
  catchError,
  of,
  map,
  Observable,
  throwError,
} from 'rxjs';
import { CacheKeyService } from '../cache-key.service';
import { DBService } from '../db.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorLevelApiService {
  protected http = inject(HttpClient);
  protected dbService = inject(DBService);
  protected cacheKeyService = inject(CacheKeyService);
  constructor() {}

  getList(cacheKey?: string) {
    return this.http
      .get(
        generateURL(
          apiPaths.errorLevelList,
          {},
          new HttpParams({ fromObject: { cacheKey } })
        )
      )
      .pipe(
        switchMap((data: any) => {
          if (data.isAllDb) {
            return from(
              appDb.errorLevel.clear().then(() =>
                appDb.errorLevel.bulkAdd(
                  data.items.map((item) => ({
                    ...item,
                    isSynced: 1,
                    isDeleted: item.isDeleted ? 1 : 0,
                  }))
                )
              )
            );
          }
          return from(
            appDb.errorLevel.bulkPut(
              data.items.map((item) => ({
                ...item,
                isSynced: 1,
                isDeleted: item.isDeleted ? 1 : 0,
              }))
            )
          );
        }),
        catchError(() => {
          return of(null);
        })
      );
  }
  create(data): Observable<{ isOffline: boolean } | { [_: string]: any }> {
    return this.http.post(generateURL(apiPaths.errorLevel), data).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'errorLevelKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('errorLevelKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }
  update(id, data) {
    return this.http.put(generateURL(apiPaths.errorLevelId, { id }), data).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'errorLevelKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('errorLevelKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }
  delete(id) {
    return this.http.delete(generateURL(apiPaths.errorLevelId, { id })).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'errorLevelKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('errorLevelKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }
  bulk(errorLevels) {
    return this.http.post(
      generateURL(apiPaths.errorLevelBulk),
      errorLevels.map((item) => ({
        ...item,
        isDeleted: item.isDeleted ? true : false,
      }))
    );
  }
}
